import React, { useEffect, useState, createContext, useContext } from 'react';
import { useAuth } from '../auth/AuthContext';
export interface Message {
  id: string;
  senderId: string;
  senderName: string;
  senderRole: string;
  recipientId: string;
  recipientName: string;
  recipientRole: string;
  jobId?: string;
  jobName?: string;
  content: string;
  timestamp: string;
  read: boolean;
  urgent: boolean;
}
export interface Conversation {
  id: string;
  participantId: string;
  participantName: string;
  participantRole: string;
  participantAvatar?: string;
  jobId?: string;
  jobName?: string;
  lastMessage?: {
    content: string;
    timestamp: string;
    isFromUser: boolean;
  };
  unreadCount: number;
}
interface MessageContextType {
  conversations: Conversation[];
  messages: Record<string, Message[]>;
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
  sendMessage: (recipientId: string, content: string, jobId?: string, urgent?: boolean) => void;
  markConversationAsRead: (conversationId: string) => void;
  getJobContacts: (jobId: string) => Conversation[];
  startNewConversation: (recipientId: string, recipientName: string, recipientRole: string, jobId?: string, jobName?: string) => string;
}
const MessageContext = createContext<MessageContextType | undefined>(undefined);
// Mock data for contacts based on roles
const MOCK_CONTACTS = [{
  id: 'foreman-1',
  name: 'John Smith',
  role: 'foreman'
}, {
  id: 'foreman-2',
  name: 'Mike Johnson',
  role: 'foreman'
}, {
  id: 'broker-1',
  name: 'Sarah Williams',
  role: 'broker'
}, {
  id: 'broker-2',
  name: 'David Chen',
  role: 'broker'
}, {
  id: 'trucking-1',
  name: 'Mountain Valley Trucking',
  role: 'trucking'
}, {
  id: 'trucking-2',
  name: 'Alpine Hauling',
  role: 'trucking'
}, {
  id: 'driver-1',
  name: 'Mike Rodriguez',
  role: 'driver'
}, {
  id: 'driver-2',
  name: 'Tom Williams',
  role: 'driver'
}, {
  id: 'contractor-1',
  name: 'ABC Construction',
  role: 'contractor'
}, {
  id: 'broker-3',
  name: 'XYZ Brokerage',
  role: 'broker'
}];
// Mock job contacts mapping - in a real app this would come from your database
const JOB_CONTACTS: Record<string, string[]> = {
  'job-1': ['foreman-1', 'broker-1', 'trucking-1', 'driver-1', 'contractor-1'],
  'job-2': ['foreman-2', 'broker-1', 'trucking-1', 'driver-2', 'contractor-1'],
  'job-3': ['foreman-1', 'broker-2', 'trucking-2', 'driver-1', 'broker-3'],
  'job-4': ['foreman-2', 'broker-2', 'trucking-2', 'driver-2', 'broker-3']
};
// Mock job names
const JOB_NAMES: Record<string, string> = {
  'job-1': 'Downtown Commercial Complex Phase 1',
  'job-2': 'Highway 89 Expansion Project',
  'job-3': 'Residential Development Phase 2',
  'job-4': 'Mountain View Condos Site Prep'
};
export const MessageProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    user
  } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Record<string, Message[]>>({});
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  // Initialize with mock data on first load
  useEffect(() => {
    if (user) {
      const mockConversations: Conversation[] = [{
        id: 'conv-1',
        participantId: 'foreman-1',
        participantName: 'John Smith',
        participantRole: 'foreman',
        jobId: 'job-1',
        jobName: 'Downtown Commercial Complex Phase 1',
        lastMessage: {
          content: 'We need more gravel on the south side',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          isFromUser: false
        },
        unreadCount: 1
      }, {
        id: 'conv-2',
        participantId: 'broker-1',
        participantName: 'Sarah Williams',
        participantRole: 'broker',
        lastMessage: {
          content: 'Your schedule has been updated for tomorrow',
          timestamp: new Date(Date.now() - 86400000).toISOString(),
          isFromUser: false
        },
        unreadCount: 0
      }, {
        id: 'conv-3',
        participantId: 'trucking-1',
        participantName: 'Mountain Valley Trucking',
        participantRole: 'trucking',
        jobId: 'job-2',
        jobName: 'Highway 89 Expansion Project',
        lastMessage: {
          content: 'Can you send another truck to the Highway 89 site?',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          isFromUser: true
        },
        unreadCount: 0
      }];
      const mockMessages: Record<string, Message[]> = {
        'conv-1': [{
          id: 'msg-1',
          senderId: 'foreman-1',
          senderName: 'John Smith',
          senderRole: 'foreman',
          recipientId: user.id,
          recipientName: user.name,
          recipientRole: user.role,
          jobId: 'job-1',
          jobName: 'Downtown Commercial Complex Phase 1',
          content: 'We need more gravel on the south side',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          read: false,
          urgent: true
        }, {
          id: 'msg-2',
          senderId: user.id,
          senderName: user.name,
          senderRole: user.role,
          recipientId: 'foreman-1',
          recipientName: 'John Smith',
          recipientRole: 'foreman',
          jobId: 'job-1',
          jobName: 'Downtown Commercial Complex Phase 1',
          content: "I, ll, check, with: dispatch, about, getting, another, load, ',: timestamp, new: Date(Date.now() - 3500000).toISOString()",
          read: true,
          urgent: false
        }],
        'conv-2': [{
          id: 'msg-3',
          senderId: 'broker-1',
          senderName: 'Sarah Williams',
          senderRole: 'broker',
          recipientId: user.id,
          recipientName: user.name,
          recipientRole: user.role,
          content: 'Your schedule has been updated for tomorrow',
          timestamp: new Date(Date.now() - 86400000).toISOString(),
          read: true,
          urgent: false
        }, {
          id: 'msg-4',
          senderId: user.id,
          senderName: user.name,
          senderRole: user.role,
          recipientId: 'broker-1',
          recipientName: 'Sarah Williams',
          recipientRole: 'broker',
          content: 'Thanks for the update',
          timestamp: new Date(Date.now() - 85000000).toISOString(),
          read: true,
          urgent: false
        }],
        'conv-3': [{
          id: 'msg-5',
          senderId: user.id,
          senderName: user.name,
          senderRole: user.role,
          recipientId: 'trucking-1',
          recipientName: 'Mountain Valley Trucking',
          recipientRole: 'trucking',
          jobId: 'job-2',
          jobName: 'Highway 89 Expansion Project',
          content: 'Can you send another truck to the Highway 89 site?',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          read: true,
          urgent: true
        }, {
          id: 'msg-6',
          senderId: 'trucking-1',
          senderName: 'Mountain Valley Trucking',
          senderRole: 'trucking',
          recipientId: user.id,
          recipientName: user.name,
          recipientRole: user.role,
          jobId: 'job-2',
          jobName: 'Highway 89 Expansion Project',
          content: 'We have a truck available and will send it right away',
          timestamp: new Date(Date.now() - 7000000).toISOString(),
          read: true,
          urgent: false
        }]
      };
      setConversations(mockConversations);
      setMessages(mockMessages);
    }
  }, [user]);
  const sendMessage = (recipientId: string, content: string, jobId?: string, urgent: boolean = false) => {
    if (!user) return;
    // Find the conversation or create a new one
    let conversationId = conversations.find(c => c.participantId === recipientId)?.id;
    if (!conversationId) {
      // Find recipient details
      const recipient = MOCK_CONTACTS.find(c => c.id === recipientId);
      if (!recipient) return;
      // Create a new conversation
      conversationId = `conv-${Date.now()}`;
      const jobName = jobId ? JOB_NAMES[jobId] : undefined;
      const newConversation: Conversation = {
        id: conversationId,
        participantId: recipientId,
        participantName: recipient.name,
        participantRole: recipient.role,
        jobId,
        jobName,
        unreadCount: 0
      };
      setConversations(prev => [...prev, newConversation]);
      setMessages(prev => ({
        ...prev,
        [conversationId]: []
      }));
    }
    // Create the new message
    const newMessage: Message = {
      id: `msg-${Date.now()}`,
      senderId: user.id,
      senderName: user.name,
      senderRole: user.role,
      recipientId,
      recipientName: conversations.find(c => c.participantId === recipientId)?.participantName || '',
      recipientRole: conversations.find(c => c.participantId === recipientId)?.participantRole || '',
      jobId,
      jobName: jobId ? JOB_NAMES[jobId] : undefined,
      content,
      timestamp: new Date().toISOString(),
      read: false,
      urgent
    };
    // Update messages
    setMessages(prev => ({
      ...prev,
      [conversationId]: [...(prev[conversationId] || []), newMessage]
    }));
    // Update conversation with last message
    setConversations(prev => prev.map(conv => conv.id === conversationId ? {
      ...conv,
      lastMessage: {
        content,
        timestamp: new Date().toISOString(),
        isFromUser: true
      }
    } : conv));
    // In a real app, you would send this message to your backend here
    console.log('Message sent:', newMessage);
  };
  const markConversationAsRead = (conversationId: string) => {
    // Mark all messages in the conversation as read
    setMessages(prev => ({
      ...prev,
      [conversationId]: prev[conversationId]?.map(msg => ({
        ...msg,
        read: true
      })) || []
    }));
    // Update unread count in conversation
    setConversations(prev => prev.map(conv => conv.id === conversationId ? {
      ...conv,
      unreadCount: 0
    } : conv));
  };
  const getJobContacts = (jobId: string): Conversation[] => {
    // Get all contacts associated with a specific job
    const contactIds = JOB_CONTACTS[jobId] || [];
    // Filter out the current user
    const filteredContactIds = contactIds.filter(id => id !== user?.id);
    // Create conversations for each contact if they don't exist
    const jobConversations: Conversation[] = [];
    filteredContactIds.forEach(contactId => {
      // Check if conversation already exists
      const existingConv = conversations.find(c => c.participantId === contactId);
      if (existingConv) {
        jobConversations.push(existingConv);
      } else {
        // Find contact details
        const contact = MOCK_CONTACTS.find(c => c.id === contactId);
        if (contact) {
          // Create a new conversation object (but don't add it to state yet)
          jobConversations.push({
            id: `conv-${contactId}-${Date.now()}`,
            participantId: contactId,
            participantName: contact.name,
            participantRole: contact.role,
            jobId,
            jobName: JOB_NAMES[jobId],
            unreadCount: 0
          });
        }
      }
    });
    return jobConversations;
  };
  const startNewConversation = (recipientId: string, recipientName: string, recipientRole: string, jobId?: string, jobName?: string): string => {
    // Check if conversation already exists
    const existingConv = conversations.find(c => c.participantId === recipientId);
    if (existingConv) {
      return existingConv.id;
    }
    // Create a new conversation
    const conversationId = `conv-${Date.now()}`;
    const newConversation: Conversation = {
      id: conversationId,
      participantId: recipientId,
      participantName: recipientName,
      participantRole: recipientRole,
      jobId,
      jobName,
      unreadCount: 0
    };
    setConversations(prev => [...prev, newConversation]);
    setMessages(prev => ({
      ...prev,
      [conversationId]: []
    }));
    return conversationId;
  };
  return <MessageContext.Provider value={{
    conversations,
    messages,
    activeConversationId,
    setActiveConversationId,
    sendMessage,
    markConversationAsRead,
    getJobContacts,
    startNewConversation
  }} data-id="element-358">
      {children}
    </MessageContext.Provider>;
};
export const useMessages = () => {
  const context = useContext(MessageContext);
  if (context === undefined) {
    throw new Error('useMessages must be used within a MessageProvider');
  }
  return context;
};